import { BiometryType } from "@capgo/capacitor-native-biometric";
import { Blac } from "blac-next";
import React from "react";
import { BiometricVerificationBloc } from "src/hybrid/components/BiometricVerification";
import { useBloc } from "src/state/state";
import {
  DebugBadge,
  DebugButton,
  DebugButtonRow,
  DebugFormGroup,
  DebugInfoBox,
  DebugKeyValue
} from "./debug.styles";

const STORAGE_ENABLED_KEY = "biometricEnabled";

const BIOMETRY_TYPE_LABEL: Record<number, string> = {
  [BiometryType.NONE]: "NONE",
  [BiometryType.TOUCH_ID]: "TOUCH_ID",
  [BiometryType.FACE_ID]: "FACE_ID",
  [BiometryType.FINGERPRINT]: "FINGERPRINT",
  [BiometryType.FACE_AUTHENTICATION]: "FACE_AUTHENTICATION",
  [BiometryType.IRIS_AUTHENTICATION]: "IRIS_AUTHENTICATION",
  [BiometryType.MULTIPLE]: "MULTIPLE",
  [BiometryType.DEVICE_CREDENTIAL]: "DEVICE_CREDENTIAL"
};

type BioState = {
  isBiometricVerificationEnabled: boolean;
  isBiometricVerificationAvailable: boolean;
  biometricType: BiometryType;
  isSessionVerified: boolean;
};

// `emit` is protected on Cubit — debug-only escape hatch to force state
// without re-running the native availability/permission checks.
type BioStateForcer = {
  emit: (next: BiometricVerificationBloc["state"]) => void;
  state: BiometricVerificationBloc["state"];
};

const patch = (partial: Partial<BioState>): void => {
  const bloc = Blac.getBloc(
    BiometricVerificationBloc
  ) as unknown as BioStateForcer;
  bloc.emit({ ...bloc.state, ...partial });
};

const DebugBiometric: React.FC = () => {
  const [bioState] = useBloc(BiometricVerificationBloc);

  const setAvailable = (next: boolean): void => {
    patch({ isBiometricVerificationAvailable: next });
  };

  const setEnabled = (next: boolean): void => {
    localStorage.setItem(STORAGE_ENABLED_KEY, String(next));
    patch({ isBiometricVerificationEnabled: next });
  };

  const setSessionVerified = (next: boolean): void => {
    // Also flip the bloc's instance flag, used by `verifyIdentity`'s short circuit.
    (
      Blac.getBloc(BiometricVerificationBloc) as unknown as {
        sessionVerified: boolean;
      }
    ).sessionVerified = next;
    patch({ isSessionVerified: next });
  };

  const setType = (type: BiometryType): void => {
    patch({ biometricType: type });
  };

  const reset = (): void => {
    localStorage.removeItem(STORAGE_ENABLED_KEY);
    patch({
      isBiometricVerificationEnabled: false,
      isBiometricVerificationAvailable: false,
      isSessionVerified: false,
      biometricType: BiometryType.NONE
    });
  };

  return (
    <div>
      <DebugKeyValue>
        <span className="key">Available</span>
        <span className="value">
          <DebugBadge
            variant={
              bioState.isBiometricVerificationAvailable ? "success" : "info"
            }
          >
            {bioState.isBiometricVerificationAvailable ? "true" : "false"}
          </DebugBadge>
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Enabled (opted in)</span>
        <span className="value">
          <DebugBadge
            variant={
              bioState.isBiometricVerificationEnabled ? "success" : "info"
            }
          >
            {bioState.isBiometricVerificationEnabled ? "true" : "false"}
          </DebugBadge>
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Session Verified</span>
        <span className="value">
          <DebugBadge variant={bioState.isSessionVerified ? "success" : "info"}>
            {bioState.isSessionVerified ? "true" : "false"}
          </DebugBadge>
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Type</span>
        <span className="value">
          <DebugBadge variant="info">
            {BIOMETRY_TYPE_LABEL[bioState.biometricType] ?? "UNKNOWN"}
          </DebugBadge>
        </span>
      </DebugKeyValue>

      <DebugInfoBox variant="info" style={{ marginTop: "0.75rem" }}>
        <p>
          Mock the values the bloc would normally derive from the native plugin.{" "}
          <code>Enabled</code> / <code>Session Verified</code> persist to
          local/session storage so they survive reload; <code>Available</code>{" "}
          and <code>Type</code> are state-only. On real web the bloc's native
          plugin checks are skipped so mocks aren't overwritten; on a real
          device the next native availability check will re-derive these.
        </p>
      </DebugInfoBox>

      <DebugButtonRow style={{ marginTop: "0.75rem" }}>
        <DebugButton
          onClick={() =>
            setAvailable(!bioState.isBiometricVerificationAvailable)
          }
        >
          Toggle Available
        </DebugButton>
        <DebugButton
          onClick={() => setEnabled(!bioState.isBiometricVerificationEnabled)}
        >
          Toggle Enabled
        </DebugButton>
        <DebugButton
          onClick={() => setSessionVerified(!bioState.isSessionVerified)}
        >
          Toggle Verified
        </DebugButton>
      </DebugButtonRow>

      <DebugFormGroup style={{ marginTop: "0.75rem" }}>
        <label>Biometric Type</label>
        <select
          value={bioState.biometricType}
          onChange={(e) => setType(Number(e.target.value))}
        >
          {Object.entries(BIOMETRY_TYPE_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </DebugFormGroup>

      <DebugButtonRow style={{ marginTop: "0.75rem" }}>
        <DebugButton className="danger" onClick={reset}>
          Reset Mock
        </DebugButton>
      </DebugButtonRow>
    </div>
  );
};

export default DebugBiometric;
